import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Money } from "@/components/HideValues";
import { PeriodPicker, PERIOD_RANGES } from "@/components/PeriodPicker";
import { LossDialog } from "@/components/Losses";
import { Loader2, Pencil, Plus, Trash2, TrendingDown } from "lucide-react";

const br = (s) => (s ? s.split("-").reverse().join("/") : "");

function LossGroup({ title, rows, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-card2-border bg-card2 p-4">
      <div className="text-[11px] font-mono-caps uppercase text-zinc-500 mb-2">{title}</div>
      {!rows.length ? <div className="text-sm text-zinc-500">Nenhum prejuízo no período.</div> : (
        <ul className="divide-y divide-zinc-800">{rows.map((r) => (
          <li key={r.name} data-testid={`${testid}-${r.name}`} className="flex items-center justify-between py-1.5 text-sm">
            <span className="text-zinc-200">{r.name} <span className="text-xs text-zinc-500">· {r.count}</span></span>
            <span className="tabular-nums text-red-300 font-bold"><Money value={r.total} /></span>
          </li>
        ))}</ul>
      )}
    </div>
  );
}

export default function OwnerLosses() {
  const [params] = useSearchParams();
  const [preset, setPreset] = useState(params.get("from") ? "custom" : "month");
  const [[from, to], setRange] = useState(() => (params.get("from") ? [params.get("from"), params.get("to") || params.get("from")] : PERIOD_RANGES[2].range()));
  const [d, setD] = useState(null);
  const [dlg, setDlg] = useState({ open: false, loss: null });
  const [key, setKey] = useState(0);
  useEffect(() => { setD(null); api.get("/losses", { params: { from, to } }).then(({ data }) => setD(data)).catch((e) => { toast.error(formatApiError(e)); setD({ items: [], total: 0 }); }); }, [from, to, key]);
  const del = async (l) => {
    if (!window.confirm(`Excluir o prejuízo de ${br(l.date)}?`)) { return; }
    try { await api.delete(`/losses/${l.id}`); toast.success("Prejuízo excluído"); setKey((k) => k + 1); } catch (e) { toast.error(formatApiError(e)); }
  };
  const th = "px-3 py-2 text-left text-[11px] font-mono-caps uppercase text-zinc-500";
  return (
    <div className="space-y-5 animate-fade-up" data-testid="losses-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono-caps text-zinc-500 uppercase"><TrendingDown className="h-4 w-4" /> Financeiro</div>
          <h1 className="font-display text-3xl font-black uppercase text-zinc-100 mt-1">Prejuízos</h1>
        </div>
        <Button data-testid="losses-new" onClick={() => setDlg({ open: true, loss: null })} className="bg-red-700 hover:bg-red-600 text-white font-bold"><Plus className="h-4 w-4 mr-1" /> Lançar prejuízo</Button>
      </div>
      <PeriodPicker preset={preset} setPreset={setPreset} from={from} to={to} setRange={setRange} testid="losses-period" />
      {d && (
        <div className="grid md:grid-cols-2 gap-3" data-testid="losses-summary">
          <LossGroup title="Por motivo" rows={d.by_reason || []} testid="losses-by-reason" />
          <LossGroup title="Por mecânico" rows={d.by_mechanic || []} testid="losses-by-mechanic" />
        </div>
      )}
      <div className="rounded-2xl border border-card2-border bg-card2 overflow-x-auto">
        {!d ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-green-brand" /></div> : (
          <table className="w-full text-sm" data-testid="losses-table">
            <thead><tr className="border-b border-zinc-800">{["Data", "Valor", "Motivo", "Mecânico", "OS", "Observação", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
            <tbody>
              {d.items.map((l) => (
                <tr key={l.id} data-testid={`loss-row-${l.id}`} className="border-b border-zinc-900">
                  <td className="px-3 py-2 text-zinc-200">{br(l.date)}</td>
                  <td className="px-3 py-2 tabular-nums text-red-300"><Money value={l.amount} /></td>
                  <td className="px-3 py-2 text-zinc-300">{l.reason}{l.reason_text ? `: ${l.reason_text}` : ""}</td>
                  <td className="px-3 py-2 text-zinc-300">{l.mechanic_name || "—"}</td>
                  <td className="px-3 py-2">{l.order_id ? <Link to={`/owner/ordens/${l.order_id}`} className="text-green-300 hover:underline">{l.order_number != null ? `#${l.order_number}` : "abrir"}</Link> : "—"}</td>
                  <td className="px-3 py-2 text-zinc-400">{l.note || ""}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button type="button" data-testid={`loss-edit-${l.id}`} onClick={() => setDlg({ open: true, loss: l })} className="h-8 px-2 rounded-md border border-zinc-700 text-xs text-zinc-200 inline-flex items-center gap-1 mr-1"><Pencil className="h-3 w-3" /> Editar</button>
                    <button type="button" data-testid={`loss-delete-${l.id}`} onClick={() => del(l)} className="h-8 px-2 rounded-md border border-red-800 text-xs text-red-300 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Excluir</button>
                  </td>
                </tr>
              ))}
              {!d.items.length && <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-500" data-testid="losses-empty">Nenhum prejuízo no período.</td></tr>}
            </tbody>
            {!!d.items.length && <tfoot><tr className="font-bold text-zinc-100" data-testid="losses-total"><td className="px-3 py-2">Total</td><td className="px-3 py-2 tabular-nums text-red-300"><Money value={d.total} /></td><td colSpan={5} /></tr></tfoot>}
          </table>
        )}
      </div>
      <LossDialog open={dlg.open} loss={dlg.loss} onClose={() => setDlg({ open: false, loss: null })} onSaved={() => { setDlg({ open: false, loss: null }); setKey((k) => k + 1); }} />
    </div>
  );
}
